export const THEME_STORAGE_KEY = 'aot-opsai-theme';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export function sanitizeThemePreference(value: unknown): ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

export function readThemePreference(storage?: Pick<Storage, 'getItem'> | null): ThemePreference {
  try {
    return sanitizeThemePreference(storage?.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
}

export function persistThemePreference(
  preference: ThemePreference,
  storage?: Pick<Storage, 'setItem'> | null,
) {
  try {
    storage?.setItem(THEME_STORAGE_KEY, sanitizeThemePreference(preference));
  } catch {
    // Theme persistence is optional; the active session remains usable.
  }
}

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  return preference === 'system' ? (systemPrefersDark ? 'dark' : 'light') : preference;
}

export function nextExplicitTheme(resolved: ResolvedTheme): ThemePreference {
  return resolved === 'dark' ? 'light' : 'dark';
}

export function themeColor(theme: ResolvedTheme) {
  return theme === 'dark' ? '#121719' : '#f3f6f5';
}

export type ThemeMediaQuery = {
  matches: boolean;
  addEventListener: (type: 'change', listener: (event: { matches: boolean }) => void) => void;
  removeEventListener: (type: 'change', listener: (event: { matches: boolean }) => void) => void;
};

export function observeSystemTheme(
  media: ThemeMediaQuery,
  onChange: (prefersDark: boolean) => void,
) {
  const listener = (event: { matches: boolean }) => onChange(event.matches);
  onChange(media.matches);
  media.addEventListener('change', listener);
  return () => media.removeEventListener('change', listener);
}

export function applyThemeToDocument(
  preference: ThemePreference,
  resolved: ResolvedTheme,
  documentValue: Pick<Document, 'documentElement' | 'querySelector'> = document,
) {
  const root = documentValue.documentElement;
  root.dataset.theme = resolved;
  root.dataset.themePreference = preference;
  root.style.colorScheme = resolved;
  documentValue
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute('content', themeColor(resolved));
}
